import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiPlus, HiUser, HiTrash, HiPencil } from 'react-icons/hi';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

export default function DriverManagement() {
  const [drivers, setDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const token = localStorage.getItem("adminToken");

  // Fetch drivers
  const fetchDrivers = async () => {
    try {
      const response = await fetch('https://afric-fresh-backend.onrender.com/admin/driver/list' ,{
        headers: {
          Authorization: `Bearer ${token}`
        }},);
      const data = await response.json();
      setDrivers(data);
    } catch (error) {
      toast.error('Failed to fetch drivers');
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Add Driver
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await fetch('https://afric-fresh-backend.onrender.com/admin/driver/add', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      setDrivers([...drivers, result]);
      reset();
      setActiveTab('list');
      Swal.fire({
        icon: 'success',
        title: 'Driver Registered',
        text: 'Driver will receive their login details via email.',
      });
    } catch (error) {
      toast.error('Failed to register driver');
    } finally {
      setLoading(false);
    }
  };

  // Delete Driver with SweetAlert Confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this driver. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });
  
    if (result.isConfirmed) {
      try {
        await fetch(`https://afric-fresh-backend.onrender.com/admin/driver/delete/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setDrivers(drivers.filter(driver => driver._id !== id));
        Swal.fire({
          icon: 'success',
          title: 'Driver Deleted',
          text: 'The driver has been deleted successfully.',
        });
      } catch (error) {
        toast.error('Failed to delete driver');
      }
    }
  };

  // Open Edit Modal
  const openEditModal = (driver) => {
    setSelectedDriver(driver);
    setIsEditModalOpen(true);
  };

  // Close Edit Modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedDriver(null);
  };

  // Update Driver
  const handleUpdate = async (data) => {
    setLoading(true);
    try {
      const response = await fetch(`https://afric-fresh-backend.onrender.com/admin/driver/edit/${selectedDriver._id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      setDrivers(drivers.map(driver => (driver._id === selectedDriver._id ? result : driver)));
      closeEditModal();
      Swal.fire({
        icon: 'success',
        title: 'Driver Updated',
        text: 'Driver details have been updated successfully.',
      });
    } catch (error) {
      toast.error('Failed to update driver');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="">
      <h1 className="text-2xl font-bold mb-6">Driver Management</h1>
      
      {/* Tabs */}
      <div className="flex space-x-4 mb-4">
        <button
          className={`p-2 px-4 rounded-lg ${activeTab === 'list' ? 'bg-gray-200 text-black' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Registered Drivers
        </button>
        <button
          className={`p-2 px-4 rounded-lg ${activeTab === 'register' ? 'bg-gray-200 text-black' : ''}`}
          onClick={() => setActiveTab('register')}
        >
          Register New Driver
        </button>
      </div>

      {activeTab === 'list' ? (
        <section className="bg-white p-6 rounded-xl shadow-sm">
          <h2 className="text-lg font-bold mb-4 flex items-center">
            <HiUser className="mr-2 text-primary" /> Registered Drivers ({drivers.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2">Name</th>
                  <th className="p-2">Email</th>
                  <th className="p-2">Phone</th>
                  <th className="p-2">Vehicle</th>
                  <th className="p-2">Plate No.</th>
                  <th className="p-2">License No.</th>
                  <th className="p-2">Bank Details</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver) => (
                  <tr key={driver.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{driver.name}</td>
                    <td className="p-2">{driver.email}</td>
                    <td className="p-2">{driver.phone}</td>
                    <td className="p-2 capitalize">{driver.vehicleType}</td>
                    <td className="p-2">{driver.plateNumber}</td>
                    <td className="p-2">{driver.licenseNumber}</td>
                    <td className="p-2">
                      {driver.bankName}, {driver.accountName}, {driver.accountNumber}
                    </td>
                    <td className="p-2 flex space-x-2">
                      <button className="text-gray-500 hover:text-primary" onClick={() => handleDelete(driver._id)}>
                        <HiTrash />
                      </button>
                      <button className="text-gray-500 hover:text-primary" onClick={() => openEditModal(driver)}>
                        <HiPencil />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="bg-white p-6 rounded-xl shadow-sm">
          <h2 className="text-lg font-bold mb-4 flex items-center">
            <HiPlus className="mr-2 text-primary" /> Register New Driver
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input type="text" placeholder="Full Name" className="w-full p-2 border rounded-lg" {...register('name', { required: true })} />
            {errors.name && <span className="text-red-500">Name is required</span>}
            <input type="email" placeholder="Email" className="w-full p-2 border rounded-lg" {...register('email', { required: true })} />
            {errors.email && <span className="text-red-500">Email is required</span>}
            <input type="tel" placeholder="Phone" className="w-full p-2 border rounded-lg" {...register('phone', { required: true })} />
            {errors.phone && <span className="text-red-500">Phone is required</span>}
            <select className="w-full p-2 border rounded-lg" {...register('vehicleType', { required: true })}>
              <option value="bike">Bike</option>
              <option value="car">Car</option>
              <option value="truck">Truck</option>
            </select>
            {errors.vehicleType && <span className="text-red-500">Vehicle type is required</span>}
            <input type="text" placeholder="Vehicle Plate Number" className="w-full p-2 border rounded-lg" {...register('plateNumber', { required: true })} />
            {errors.plateNumber && <span className="text-red-500">Plate number is required</span>}
            <input type="text" placeholder="Driver's License Number" className="w-full p-2 border rounded-lg" {...register('licenseNumber', { required: true })} />
            {errors.licenseNumber && <span className="text-red-500">License number is required</span>}
            <input type="text" placeholder="Bank Name" className="w-full p-2 border rounded-lg" {...register('bankName', { required: true })} />
            {errors.bankName && <span className="text-red-500">Bank name is required</span>}
            <input type="text" placeholder="Account Name" className="w-full p-2 border rounded-lg" {...register('accountName', { required: true })} />
            {errors.accountName && <span className="text-red-500">Account name is required</span>}
            <input type="text" placeholder="Account Number" className="w-full p-2 border rounded-lg" {...register('accountNumber', { required: true })} />
            {errors.accountNumber && <span className="text-red-500">Account number is required</span>}
            <input type="password" placeholder="Password" className="w-full p-2 border rounded-lg" {...register('password', { required: true })} />
            {errors.password && <span className="text-red-500">Password is required</span>}
            <button type="submit" className="w-full bg-cyan-600 text-white p-2 rounded-lg" disabled={loading}>
              {loading ? 'Registering...' : 'Register Driver'}
            </button>
          </form>
        </section>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white p-6 rounded-xl shadow-sm w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Edit Driver</h2>
            <form onSubmit={handleSubmit(handleUpdate)} className="space-y-4">
              <input type="text" placeholder="Full Name" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.name} {...register('name', { required: true })} />
              {errors.name && <span className="text-red-500">Name is required</span>}
              <input type="email" placeholder="Email" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.email} {...register('email', { required: true })} />
              {errors.email && <span className="text-red-500">Email is required</span>}
              <input type="tel" placeholder="Phone" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.phone} {...register('phone', { required: true })} />
              {errors.phone && <span className="text-red-500">Phone is required</span>}
              <select className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.vehicleType} {...register('vehicleType', { required: true })}>
                <option value="bike">Bike</option>
                <option value="car">Car</option>
                <option value="truck">Truck</option>
              </select>
              {errors.vehicleType && <span className="text-red-500">Vehicle type is required</span>}
              <input type="text" placeholder="Vehicle Plate Number" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.plateNumber} {...register('plateNumber', { required: true })} />
              {errors.plateNumber && <span className="text-red-500">Plate number is required</span>}
              <input type="text" placeholder="Driver's License Number" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.licenseNumber} {...register('licenseNumber', { required: true })} />
              {errors.licenseNumber && <span className="text-red-500">License number is required</span>}
              <input type="text" placeholder="Bank Name" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.bankName} {...register('bankName', { required: true })} />
              {errors.bankName && <span className="text-red-500">Bank name is required</span>}
              <input type="text" placeholder="Account Name" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.accountName} {...register('accountName', { required: true })} />
              {errors.accountName && <span className="text-red-500">Account name is required</span>}
              <input type="text" placeholder="Account Number" className="w-full p-2 border rounded-lg" defaultValue={selectedDriver.accountNumber} {...register('accountNumber', { required: true })} />
              {errors.accountNumber && <span className="text-red-500">Account number is required</span>}
              <div className="flex justify-end space-x-2">
                <button type="button" className="bg-gray-200 text-black p-2 rounded-lg" onClick={closeEditModal}>
                  Cancel
                </button>
                <button type="submit" className="bg-cyan-600 text-white p-2 rounded-lg" disabled={loading} >
                  {loading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </motion.div>
  );
}